import { type CartState, initialCartState } from "../types";
import {
  type EntryGroup,
  type KitGroup,
  type ProductGroup,
  isKitGroup,
  isPackPhaseoutGroup,
  isProductPhaseoutGroup,
} from "../../models/CartData";
import { CHECKOUT_LOAD_DATA } from "../actions/checkoutPageActions";
import { type CartActions } from "../actions/cartQuoteCommonActions";
import { userIsSalesman } from "~/domains/user/utils/roles";
import { produce } from "immer";
import { CART_LOAD_SUCCESS } from "~/features/cart/cart/redux/actions";
import { UPDATE_CART_COMMENT } from "~/features/cart/cart/redux/actions/cartPageActions";
import { isCartPackEntryValid, isQuotePackEntryValid } from "~/features/cart/cart/redux/reducers/lineItemValidations";
import { CLEAR_CART, DELETE_PACKAGE_ENTRY, SET_PACKAGE_ENTRIES } from "~/features/cart/cart/redux/actions/actionTypes";
import type { TransformedCartData } from "../../transformers/CartResponseTransformer";
import type { TransformedCheckoutData } from "../../transformers/CartResponseTransformer";

const isPackEntryValid = userIsSalesman() ? isQuotePackEntryValid : isCartPackEntryValid;

function pruneEmptyGroups(draft: CartState): void {
  const nonEmptyGroups = new Set<number>();
  Object.values(draft.packageEntriesById).forEach((e) => {
    e.groupNumbers.forEach((gn) => nonEmptyGroups.add(gn));
  });

  function hasAnyEntry(group: EntryGroup): boolean {
    if (group.groupType === "PRODUCT" || group.groupType === "KIT") {
      return nonEmptyGroups.has(group.groupNumber);
    }
    if (isPackPhaseoutGroup(group)) {
      return (
        group.predecessorPackEntryPks.some((pk) => pk in draft.packageEntriesById) ||
        group.successorPackEntryPks.some((pk) => pk in draft.packageEntriesById)
      );
    }
    return group.children.some(hasAnyEntry);
  }

  function pruneNode(group: EntryGroup): EntryGroup {
    const keptChildren = group.children.filter(hasAnyEntry);
    const prunedChildren = group.children.filter((c) => !hasAnyEntry(c));
    prunedChildren.forEach((c) => deleteGroupFromIndex(draft, c));
    const updated = { ...group, children: keptChildren.map(pruneNode) } as EntryGroup;
    draft.entryGroupsByNumber[group.groupNumber] = updated;
    return updated;
  }

  const prunedTopLevel = draft.entryGroups.filter((g) => !hasAnyEntry(g));
  prunedTopLevel.forEach((g) => deleteGroupFromIndex(draft, g));
  draft.entryGroups = draft.entryGroups.filter(hasAnyEntry).map(pruneNode);
}

function deleteGroupFromIndex(draft: CartState, group: EntryGroup): void {
  group.children.forEach((c) => deleteGroupFromIndex(draft, c));
  delete draft.entryGroupsByNumber[group.groupNumber];
}

let _packageEntriesByIdRef: CartState["packageEntriesById"];
let _entryGroupsByNumberRef: CartState["entryGroupsByNumber"];
let _nonEmptyGroupsRef: Set<number>;

function dissolveInvalidPhaseoutGroups(draft: CartState): void {
  _packageEntriesByIdRef = draft.packageEntriesById;
  _entryGroupsByNumberRef = draft.entryGroupsByNumber;
  _nonEmptyGroupsRef = new Set<number>();
  Object.values(draft.packageEntriesById).forEach((e) => {
    e.groupNumbers.forEach((gn) => _nonEmptyGroupsRef.add(gn));
  });

  for (let i = 0; i < draft.entryGroups.length; i++) {
    const rootGroup = draft.entryGroups[i];
    dissolvePackPhaseouts(rootGroup, draft);
    dissolveProductPhaseouts(rootGroup, draft);
  }
}

function dissolvePackPhaseouts(rootGroup: EntryGroup, draft: CartState): void {
  for (const child of rootGroup.children) {
    if (child.groupType !== "PRODUCT") {
      continue;
    }

    const packPhaseout = child.children.find(isPackPhaseoutGroup);
    if (!packPhaseout) {
      continue;
    }

    const predecessorAlive = packPhaseout.predecessorPackEntryPks.some((pk) => pk in _packageEntriesByIdRef);
    const successorAlive = packPhaseout.successorPackEntryPks.some((pk) => pk in _packageEntriesByIdRef);

    if (predecessorAlive && successorAlive) {
      continue;
    }

    child.children = child.children.filter((c) => c.groupNumber !== packPhaseout.groupNumber);
    deleteGroupFromIndex(draft, packPhaseout);
  }
}

function dissolveProductPhaseouts(rootGroup: EntryGroup, draft: CartState): void {
  const toDissolve = new Set<number>();

  for (const child of rootGroup.children) {
    if (!isProductPhaseoutGroup(child)) {
      continue;
    }

    const predecessorAlive = child.predecessorGroupNumbers.some((gn) => {
      const g = _entryGroupsByNumberRef[gn];
      if (!g || g.groupType !== "PRODUCT") {
        return false;
      }
      return _nonEmptyGroupsRef.has(gn);
    });
    const successorAlive = child.successorGroupNumbers.some((gn) => {
      const g = _entryGroupsByNumberRef[gn];
      if (!g || g.groupType !== "PRODUCT") {
        return false;
      }
      return _nonEmptyGroupsRef.has(gn);
    });

    if (!predecessorAlive || !successorAlive) {
      toDissolve.add(child.groupNumber);
    }
  }

  if (toDissolve.size === 0) {
    return;
  }

  const survivingChildren: EntryGroup[] = [];
  const newChildren: EntryGroup[] = [];

  for (const child of rootGroup.children) {
    if (toDissolve.has(child.groupNumber)) {
      for (const grandchild of child.children) {
        if (grandchild.groupType === "PRODUCT") {
          const hasEntries = _nonEmptyGroupsRef.has(grandchild.groupNumber);
          if (hasEntries) {
            survivingChildren.push(grandchild);
          } else {
            deleteGroupFromIndex(draft, grandchild);
          }
        } else {
          deleteGroupFromIndex(draft, grandchild);
        }
      }
      delete draft.entryGroupsByNumber[child.groupNumber];
      _nonEmptyGroupsRef.delete(child.groupNumber);
    } else {
      newChildren.push(child);
    }
  }

  rootGroup.children = [...newChildren, ...survivingChildren];
}

export const cartReducer = (state = initialCartState, action: CartActions): CartState => {
  switch (action.type) {
    case CART_LOAD_SUCCESS: {
      const data = action.payload as unknown as TransformedCartData;

      return {
        ...state,
        packageEntries: data.packageEntries,
        packageEntriesById: data.packageEntriesById,
        entryGroups: data.entryGroups,
        entryGroupsByNumber: data.entryGroupsByNumber,
        comment: data.comment,
      };
    }

    case CHECKOUT_LOAD_DATA: {
      const { data } = action.payload as unknown as { data: TransformedCheckoutData };

      return {
        ...state,
        packageEntries: data.packageEntries,
        packageEntriesById: data.packageEntriesById,
        entryGroups: data.entryGroups,
        entryGroupsByNumber: data.entryGroupsByNumber,
      };
    }

    case SET_PACKAGE_ENTRIES: {
      const data = action.payload;

      return produce(state, (draft) => {
        // Keep temporary groups independent of entry pks and existing server group numbers.
        let nextSyntheticGroupNumber = Number.MIN_SAFE_INTEGER;
        const allocateGroupNumber = () => {
          while (nextSyntheticGroupNumber in draft.entryGroupsByNumber) {
            nextSyntheticGroupNumber++;
          }
          return nextSyntheticGroupNumber++;
        };

        data.forEach((item) => {
          const packageEntry = { ...item.packageEntry };
          packageEntry.valid = isPackEntryValid(packageEntry);
          packageEntry.disableEdit =
            (packageEntry.contractErrorMessage !== undefined && packageEntry.contractErrorMessage.type === "fatal") ||
            packageEntry.messages.some((msg) => msg.type === "fatal");

          if (packageEntry.groupNumbers.length === 0) {
            const existingOrderable = draft.entryGroups.find((g) => g.groupType === "ORDERABLE");
            let orderableGroup: EntryGroup;
            if (existingOrderable) {
              orderableGroup = existingOrderable as EntryGroup;
            } else {
              const syntheticOrderableNumber = allocateGroupNumber();
              const newOrderable: EntryGroup = {
                groupType: "ORDERABLE",
                groupNumber: syntheticOrderableNumber,
                children: [],
              } as EntryGroup;
              draft.entryGroups.push(newOrderable);
              draft.entryGroupsByNumber[syntheticOrderableNumber] = newOrderable;
              orderableGroup = newOrderable;
            }
            let productGroup = (orderableGroup.children as EntryGroup[]).find(
              (g): g is ProductGroup =>
                g.groupType === "PRODUCT" && (g as ProductGroup).productCode === item.productCode,
            );
            if (!productGroup) {
              const syntheticGroupNumber = allocateGroupNumber();
              productGroup = {
                groupType: "PRODUCT",
                groupNumber: syntheticGroupNumber,
                productCode: item.productCode,
                children: [],
              } as ProductGroup;
              orderableGroup.children.push(productGroup);
              draft.entryGroupsByNumber[syntheticGroupNumber] = productGroup;
            }
            packageEntry.groupNumbers = [orderableGroup.groupNumber, productGroup.groupNumber];
          }

          draft.packageEntriesById[packageEntry.pk] = packageEntry;
          if (!draft.packageEntries.includes(packageEntry.pk)) {
            draft.packageEntries.push(packageEntry.pk);
          }
        });
      });
    }

    case DELETE_PACKAGE_ENTRY: {
      const { packageEntryId } = action.payload;

      return produce(state, (draft) => {
        const entry = draft.packageEntriesById[packageEntryId];
        if (!entry) {
          console.warn(`Tried to delete package with pk ${packageEntryId} but it's already deleted!`);
          return;
        }

        const entriesToDelete: number[] = [packageEntryId];

        const kitGroupNumber = entry.groupNumbers.find((n) => isKitGroup(draft.entryGroupsByNumber[n] as EntryGroup));

        if (kitGroupNumber !== undefined) {
          const kitGroup = draft.entryGroupsByNumber[kitGroupNumber] as KitGroup;
          if (entry.pk === kitGroup.housingEntryPk) {
            Object.values(draft.packageEntriesById)
              .filter((e) => e.groupNumbers.includes(kitGroupNumber) && e.pk !== packageEntryId)
              .forEach((e) => entriesToDelete.push(e.pk));
          }
        }

        entriesToDelete.forEach((pk) => {
          delete draft.packageEntriesById[pk];
        });
        draft.packageEntries = draft.packageEntries.filter((pk) => !entriesToDelete.includes(pk));

        pruneEmptyGroups(draft);
        dissolveInvalidPhaseoutGroups(draft);
      });
    }

    case CLEAR_CART: {
      return initialCartState;
    }

    case UPDATE_CART_COMMENT: {
      const comment = action.payload;
      return {
        ...state,
        comment,
      };
    }

    default:
      return state;
  }
};
